import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PlayerPerformanceCharts } from '../components/PlayerPerformanceCharts';
import { PlayerSeasonHighlight } from '../components/PlayerSeasonHighlight';
import { toast } from 'sonner';
import { formatDateUK } from '../lib/utils';
import { 
  ArrowLeft,
  User,
  Target,
  Trophy,
  Clock,
  Award,
  Users,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Square,
  Play,
  Calendar,
  PoundSterling,
  Edit2,
  Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlayerDetail() {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [player, setPlayer] = useState(null);
  const [matches, setMatches] = useState([]);
  const [trainingStats, setTrainingStats] = useState(null);
  const [duesStats, setDuesStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalPlayedMatches, setTotalPlayedMatches] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({ name: '', position: '', jersey_number: '', status: 'available' });

  const POSITIONS = [
    'Goalkeeper', 'Right Back', 'Left Back', 'Center Back',
    'Defensive Midfielder', 'Central Midfielder', 'Attacking Midfielder',
    'Right Wing', 'Left Wing', 'Striker', 'Forward'
  ];

  const PLAYER_STATUSES = [
    { value: 'available', label: 'Available', color: 'bg-emerald-100 text-emerald-700' },
    { value: 'unavailable', label: 'Unavailable', color: 'bg-gray-100 text-gray-700' },
    { value: 'injured', label: 'Injured', color: 'bg-red-100 text-red-700' },
    { value: 'suspended', label: 'Suspended', color: 'bg-amber-100 text-amber-700' },
    { value: 'transferred', label: 'Transferred', color: 'bg-blue-100 text-blue-700' }
  ];

  const getStatusStyle = (status) => {
    const statusObj = PLAYER_STATUSES.find(s => s.value === status);
    return statusObj || { label: 'Available', color: 'bg-emerald-100 text-emerald-700' };
  };

  useEffect(() => {
    if (playerId) {
      fetchPlayerData();
    }
  }, [playerId]);

  const fetchPlayerData = async () => {
    try {
      const [playerRes, matchesRes, trainingRes, duesRes] = await Promise.all([
        axios.get(`${API}/players/${playerId}`),
        axios.get(`${API}/players/${playerId}/matches`),
        axios.get(`${API}/players/${playerId}/training-stats`),
        axios.get(`${API}/players/${playerId}/dues-stats`)
      ]);
      setPlayer(playerRes.data);
      const matchData = matchesRes.data;
      setMatches(matchData.matches || matchData);
      setTotalPlayedMatches(matchData.total_played_matches || 0);
      setTrainingStats(trainingRes.data);
      setDuesStats(duesRes.data);
    } catch (error) {
      console.error('Failed to fetch player data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = () => {
    if (!player) return;
    setEditFormData({
      name: player.name || '',
      position: player.position || '',
      jersey_number: player.jersey_number?.toString() || '',
      status: player.status || 'available'
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const payload = {
        name: editFormData.name,
        position: editFormData.position || null,
        jersey_number: editFormData.jersey_number ? parseInt(editFormData.jersey_number) : null,
        status: editFormData.status
      };
      const res = await axios.put(`${API}/players/${playerId}`, payload);
      setPlayer(res.data);
      setEditDialogOpen(false);
      toast.success('Player updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update player');
    }
  };

  const handleDeletePlayer = async () => {
    if (!window.confirm(`Delete ${player.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/players/${playerId}`);
      toast.success('Player deleted');
      navigate('/players');
    } catch (error) {
      toast.error('Failed to delete player');
    }
  };

  const getMatchResult = (match) => {
    if (match.status !== 'result' || match.our_score === null) return null;
    if (match.our_score > match.opponent_score) return 'W';
    if (match.our_score < match.opponent_score) return 'L';
    return 'D';
  };

  // Filter to only show past matches (where date + kick-off time has passed)
  const isMatchPlayed = (match) => {
    try {
      const matchDate = match.date;
      const kickOffTime = match.kick_off_time || '00:00';
      const matchDateTime = new Date(`${matchDate}T${kickOffTime}:00`);
      return new Date() > matchDateTime;
    } catch (e) {
      return false;
    }
  };

  const pastMatches = matches.filter(isMatchPlayed);

  // ── Appearance / discipline / production stats ─────────────────
  //
  // Computed live from the `matches` array (which now includes any match
  // with a `player_stats` entry — see /api/players/{id}/matches). The
  // stored `player.stats` aggregate is only used as a fallback when the
  // matches array hasn't loaded yet OR is genuinely empty (brand-new
  // player). Numbers always tally with the chart data below.
  // Declared BEFORE any early returns so hook order stays stable.
  //
  const stats = useMemo(() => {
    const playerStats = player?.stats || {};
    const resultMatches = (matches || []).filter((m) => m.status === 'result');
    if (resultMatches.length === 0) {
      return playerStats;
    }
    const sum = (key) => resultMatches.reduce((s, m) => s + Number(m?.stats?.[key] || 0), 0);
    const countWhere = (pred) => resultMatches.filter(pred).length;
    const DEFAULT_MATCH_MINUTES = 90;
    const totalMinutes  = sum('minutes_played');
    const appearances   = countWhere((m) => m.was_starter || m.was_sub);
    const avgMinsPerApp = appearances > 0 ? Math.round(totalMinutes / appearances) : 0;

    // ── Form-trend helpers (recent appearance window vs previous) ──
    // Used by Avg Mins, Goals, and Assists pills to show whether a
    // player is trending up or down. Window auto-shrinks (down to
    // a minimum of 3) when the player has fewer total appearances.
    const appearanceMatches = resultMatches
      .filter((m) => m.was_starter || m.was_sub)
      .slice()
      .sort((a, b) => {
        const da = new Date(`${a.date || ''}T${a.kick_off_time || '00:00'}:00`).getTime();
        const db = new Date(`${b.date || ''}T${b.kick_off_time || '00:00'}:00`).getTime();
        return db - da; // most recent first
      });
    const MIN_TREND_APPS = 6;
    let trendWindow = 0;
    let recentApps = [];
    let previousApps = [];
    if (appearanceMatches.length >= MIN_TREND_APPS) {
      trendWindow = Math.min(5, Math.floor(appearanceMatches.length / 2));
      recentApps = appearanceMatches.slice(0, trendWindow);
      previousApps = appearanceMatches.slice(trendWindow, trendWindow * 2);
    }
    const trendOf = (extract, { rounded = false } = {}) => {
      if (trendWindow === 0) return null;
      const r = recentApps.reduce((s, m) => s + Number(extract(m) || 0), 0);
      const p = previousApps.reduce((s, m) => s + Number(extract(m) || 0), 0);
      if (rounded) {
        const ra = r / recentApps.length;
        const pa = p / previousApps.length;
        return { delta: Math.round(ra - pa), window: trendWindow };
      }
      return { delta: r - p, window: trendWindow };
    };

    const avgMinsTrend = trendOf((m) => m?.stats?.minutes_played, { rounded: true });
    const goalsTrend   = trendOf((m) => m?.stats?.goals);
    const assistsTrend = trendOf((m) => m?.stats?.assists);
    return {
      goals:           sum('goals'),
      own_goals:       sum('own_goals'),
      assists:         sum('assists'),
      minutes_played:  totalMinutes,
      avg_mins_per_match: avgMinsPerApp,
      avg_mins_trend:  avgMinsTrend,
      goals_trend:     goalsTrend,
      assists_trend:   assistsTrend,
      starts:          countWhere((m) => m.was_starter),
      sub_appearances: countWhere((m) => m.was_sub),
      unused_sub:      countWhere((m) => m.was_unused_sub),
      subbed_off: countWhere((m) => {
        if (!m.was_starter) return false;
        const minutes = Number(m?.stats?.minutes_played || 0);
        if (minutes <= 0) return false;
        const matchLen = Number(m?.duration_minutes || DEFAULT_MATCH_MINUTES);
        return minutes < matchLen;
      }),
      yellow_cards:    sum('yellow_cards'),
      red_cards:       sum('red_cards'),
      player_of_match: countWhere(
        (m) => m?.stats?.is_pom || m?.stats?.was_pom || m?.stats?.player_of_match
      ),
      squad_selections: countWhere(
        (m) => m.was_starter || m.was_sub || m.was_unused_sub
      ),
    };
  }, [matches, player]);

  const getResultColor = (result) => {
    switch (result) {
      case 'W': return 'bg-emerald-500 text-white';
      case 'L': return 'bg-red-500 text-white';
      case 'D': return 'bg-amber-500 text-white';
      default: return 'bg-gray-200 text-gray-600';
    }
  };

  const getVenueLabel = (venue) => {
    switch (venue) {
      case 'home': return 'H';
      case 'away': return 'A';
      default: return 'N';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500">Loading player data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!player) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="w-20 h-20 rounded-2xl bg-red-100 flex items-center justify-center mb-6">
            <AlertCircle className="w-10 h-10 text-red-600" strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 font-title">Player Not Found</h2>
          <p className="text-gray-500 mb-6">This player doesn't exist or you don't have access.</p>
          <Button onClick={() => navigate('/players')} className="btn-primary rounded-xl">
            Back to Players
          </Button>
        </div>
      </Layout>
    );
  }

  // ── (Appearance stats moved above the early returns to satisfy
  //     the rules-of-hooks linter — see `stats = useMemo(...)` earlier.)

  // Compact form-trend pill used by Goals / Assists / Avg Mins.
  // Hidden when trend is null (not enough appearances) or delta === 0.
  const TrendPill = ({ trend, suffix = '', testid }) => {
    if (!trend || trend.delta === 0) return null;
    const positive = trend.delta > 0;
    const Icon = positive ? TrendingUp : TrendingDown;
    return (
      <span
        data-testid={testid}
        title={`Last ${trend.window} apps vs previous ${trend.window}`}
        className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-semibold ${
          positive ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'
        }`}
      >
        <Icon className="w-3 h-3" strokeWidth={2.5} />
        {positive ? '+' : ''}{trend.delta}{suffix}
      </span>
    );
  };

  return (
    <Layout>
      <div className="space-y-6 page-slide-in" data-testid="player-detail-container">
        {/* Back Button & Header */}
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/players')}
            className="mb-4 -ml-2 text-gray-600 hover:text-gray-900"
            data-testid="back-to-players-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            Back to Players
          </Button>
          
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 flex-1 min-w-0">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <User className="w-10 h-10 sm:w-12 sm:h-12 text-emerald-600" strokeWidth={1.5} />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 font-title tracking-tight">
                    {player.name}
                  </h1>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusStyle(player.status).color}`} data-testid="player-status-badge">
                    {getStatusStyle(player.status).label}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  {player.position && (
                    <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                      {player.position}
                    </span>
                  )}
                  {player.jersey_number && (
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                      #{player.jersey_number}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {isAdmin && (
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openEditDialog}
                  className="rounded-lg"
                  data-testid="edit-player-btn"
                >
                  <Edit2 className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDeletePlayer}
                  className="rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  data-testid="delete-player-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                  Delete
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Statistics Overview */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4" data-testid="player-stats">
          <Card className="stat-card">
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <Target className="w-6 h-6 text-emerald-600" strokeWidth={1.5} />
              </div>
              <p className="text-3xl font-bold text-gray-900">{stats.goals || 0}</p>
              <p className="text-sm text-gray-500">Goals</p>
              <div className="mt-1 flex items-center justify-center gap-1">
                <TrendPill trend={stats.goals_trend} testid="goals-trend" />
              </div>
              {stats.own_goals > 0 && (
                <p className="text-xs text-red-500 mt-1">{stats.own_goals} x OG</p>
              )}
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-3">
                <TrendingUp className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
              </div>
              <p className="text-3xl font-bold text-gray-900">{stats.assists || 0}</p>
              <p className="text-sm text-gray-500">Assists</p>
              <div className="mt-1 flex items-center justify-center gap-1">
                <TrendPill trend={stats.assists_trend} testid="assists-trend" />
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mx-auto mb-3">
                <Award className="w-6 h-6 text-amber-600" strokeWidth={1.5} />
              </div>
              <p className="text-3xl font-bold text-gray-900">{stats.player_of_match || 0}</p>
              <p className="text-sm text-gray-500">Player of Match</p>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mx-auto mb-3">
                <Clock className="w-6 h-6 text-purple-600" strokeWidth={1.5} />
              </div>
              <p className="text-3xl font-bold text-gray-900">{stats.minutes_played || 0}</p>
              <p className="text-sm text-gray-500">Minutes Played</p>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-indigo-600" strokeWidth={1.5} />
              </div>
              <p className="text-3xl font-bold text-gray-900">{stats.squad_selections || 0} <span className="text-lg text-gray-400">/ {totalPlayedMatches}</span></p>
              <p className="text-sm text-gray-500">Squad Selections</p>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="training-attendance-stat">
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center mx-auto mb-3">
                <Calendar className="w-6 h-6 text-teal-600" strokeWidth={1.5} />
              </div>
              <p className="text-3xl font-bold text-gray-900">{trainingStats?.attended || 0}</p>
              <p className="text-sm text-gray-500">Training Sessions</p>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <Card data-testid="appearance-stats">
            <CardHeader>
              <CardTitle className="text-xl font-semibold font-title">Appearances</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Play className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                    <span className="font-medium">Starts</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">{stats.starts || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                    <span className="font-medium">Sub Appearances</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">{stats.sub_appearances || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Square className="w-5 h-5 text-gray-500" strokeWidth={1.5} />
                    <span className="font-medium">Unused Sub</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">{stats.unused_sub || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <TrendingDown className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                    <span className="font-medium">Subbed Off</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">{stats.subbed_off || 0}</span>
                </div>
                <div
                  className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl border border-emerald-100"
                  data-testid="avg-mins-per-match"
                  title="Total minutes played ÷ appearances (starts + sub appearances)"
                >
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-emerald-700" strokeWidth={1.5} />
                    <span className="font-medium">Avg Mins / Match</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendPill
                      trend={stats.avg_mins_trend}
                      suffix="'"
                      testid="avg-mins-trend"
                    />
                    <span className="text-2xl font-bold text-emerald-700">
                      {stats.avg_mins_per_match ? `${stats.avg_mins_per_match}'` : '—'}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="discipline-stats">
            <CardHeader>
              <CardTitle className="text-xl font-semibold font-title">Discipline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-7 bg-yellow-400 rounded-sm"></div>
                    <span className="font-medium">Yellow Cards</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">{stats.yellow_cards || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-7 bg-red-500 rounded-sm"></div>
                    <span className="font-medium">Red Cards</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">{stats.red_cards || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="training-stats">
            <CardHeader>
              <CardTitle className="text-xl font-semibold font-title">Training</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-teal-600" strokeWidth={1.5} />
                    <span className="font-medium">Sessions Attended</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">{trainingStats?.attended || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-gray-500" strokeWidth={1.5} />
                    <span className="font-medium">Total Sessions</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">{trainingStats?.total_sessions || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                    <span className="font-medium">Attendance Rate</span>
                  </div>
                  <span className="text-2xl font-bold text-emerald-600">{trainingStats?.attendance_rate || 0}%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Player Dues Stats */}
          {duesStats && (duesStats.paid_count > 0 || duesStats.unpaid_count > 0) && (
            <Card data-testid="dues-stats">
              <CardHeader>
                <CardTitle className="text-xl font-semibold font-title">Player Dues</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <PoundSterling className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                      <span className="font-medium">Total Paid</span>
                    </div>
                    <span className="text-2xl font-bold text-emerald-600">£{duesStats.total_paid?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Target className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                      <span className="font-medium">Matches Paid</span>
                    </div>
                    <span className="text-2xl font-bold text-gray-900">{duesStats.paid_count || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-red-500" strokeWidth={1.5} />
                      <span className="font-medium">Matches Unpaid</span>
                    </div>
                    <span className={`text-2xl font-bold ${duesStats.unpaid_count > 0 ? 'text-red-500' : 'text-gray-900'}`}>
                      {duesStats.unpaid_count || 0}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Performance Charts — goals/assists/minutes trends + training attendance trend */}
        <PlayerPerformanceCharts matches={matches} trainingStats={trainingStats} />

        {/* Auto-generated season highlight + Share to parents */}
        <PlayerSeasonHighlight
          player={player}
          matches={matches}
          trainingStats={trainingStats}
          teamName={user?.team_name}
        />

        {/* Match History */}
        <Card data-testid="match-history">
          <CardHeader>
            <CardTitle className="text-xl font-semibold font-title">
              Match History ({pastMatches.length} matches)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pastMatches.length === 0 ? (
              <div className="text-center py-8 sm:py-12 text-gray-400">
                <Trophy className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 opacity-50" strokeWidth={1.5} />
                <p className="text-sm sm:text-base">No matches played yet this season</p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {pastMatches.map((match) => {
                  const result = getMatchResult(match);
                  const matchStats = match.stats || {};
                  
                  return (
                    <div 
                      key={match.match_id} 
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-xl gap-3 sm:gap-4"
                      data-testid={`match-history-${match.match_id}`}
                    >
                      <div className="flex items-center gap-3 sm:gap-4">
                        {/* Result Badge */}
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${getResultColor(result)}`}>
                          {result || '-'}
                        </div>
                        
                        {/* Match Info */}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">vs {match.opponent}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              match.venue === 'home' ? 'bg-emerald-100 text-emerald-700' :
                              match.venue === 'away' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {getVenueLabel(match.venue)}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                            <span>{formatDateUK(match.date)}</span>
                            <span>•</span>
                            <span>{match.competition}</span>
                            {match.status === 'result' && match.our_score !== null && (
                              <>
                                <span>•</span>
                                <span className="font-medium">{match.our_score} - {match.opponent_score}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Player's Performance in Match */}
                      <div className="flex items-center gap-4">
                        {/* Role */}
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          match.was_starter ? 'bg-emerald-100 text-emerald-700' : 
                          match.was_unused_sub ? 'bg-gray-200 text-gray-600' :
                          match.was_not_selected ? 'bg-orange-100 text-orange-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {match.was_starter ? 'Started' : match.was_unused_sub ? 'Bench' : match.was_not_selected ? 'Not Selected' : 'Sub'}
                        </span>

                        {/* Non-selection reason */}
                        {match.was_not_selected && match.non_selection_reason && (
                          <span className="text-xs text-orange-600 italic" data-testid={`non-selection-reason-${match.match_id}`}>
                            {match.non_selection_reason}
                          </span>
                        )}

                        {/* Stats for this match */}
                        {matchStats && (
                          <div className="flex items-center gap-3 text-sm">
                            {matchStats.goals > 0 && (
                              <div className="flex items-center gap-1">
                                <Target className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
                                <span className="font-medium">{matchStats.goals}</span>
                              </div>
                            )}
                            {matchStats.assists > 0 && (
                              <div className="flex items-center gap-1">
                                <TrendingUp className="w-4 h-4 text-blue-600" strokeWidth={1.5} />
                                <span className="font-medium">{matchStats.assists}</span>
                              </div>
                            )}
                            {matchStats.yellow_cards > 0 && (
                              <div className="w-4 h-5 bg-yellow-400 rounded-sm"></div>
                            )}
                            {matchStats.red_cards > 0 && (
                              <div className="w-4 h-5 bg-red-500 rounded-sm"></div>
                            )}
                            {matchStats.is_pom && (
                              <div className="flex items-center gap-1">
                                <Award className="w-4 h-4 text-amber-500" strokeWidth={1.5} />
                                <span className="text-xs text-amber-600 font-medium">POTM</span>
                              </div>
                            )}
                            {matchStats.minutes_played > 0 && (
                              <span className="text-gray-500">{matchStats.minutes_played}'</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status History */}
        {player.status_history && player.status_history.length > 0 && (
          <Card data-testid="status-history-card">
            <CardHeader>
              <CardTitle className="text-xl font-semibold font-title">Status History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-2">
                {[...player.status_history].reverse().map((entry, idx) => {
                  const style = getStatusStyle(entry.status);
                  const start = entry.start_date ? formatDateUK(entry.start_date) : '—';
                  const end = entry.end_date ? formatDateUK(entry.end_date) : 'Present';
                  return (
                    <div
                      key={`${entry.status}-${entry.start_date}-${idx}`}
                      className="flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-xl"
                      data-testid={`status-history-entry-${idx}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${style.color}`}>
                          {style.label}
                        </span>
                        <span className="text-sm text-gray-600 truncate">
                          {start} <span className="text-gray-400">→</span> {end}
                        </span>
                      </div>
                      {!entry.end_date && (
                        <span className="text-xs font-medium text-emerald-600 shrink-0">Current</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Edit Player Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-md" data-testid="edit-player-dialog">
            <DialogHeader>
              <DialogTitle>Edit Player</DialogTitle>
              <DialogDescription>Update this player's details. Stats history is preserved.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-player-name">Name</Label>
                <Input
                  id="edit-player-name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  placeholder="Player name"
                  data-testid="edit-player-name-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-player-position">Position</Label>
                <Select
                  value={editFormData.position || undefined}
                  onValueChange={(value) => setEditFormData({ ...editFormData, position: value })}
                >
                  <SelectTrigger id="edit-player-position" data-testid="edit-player-position-select">
                    <SelectValue placeholder="Select position" />
                  </SelectTrigger>
                  <SelectContent>
                    {POSITIONS.map((pos) => (
                      <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-player-jersey">Jersey Number</Label>
                <Input
                  id="edit-player-jersey"
                  type="number"
                  min="1"
                  max="99"
                  value={editFormData.jersey_number}
                  onChange={(e) => setEditFormData({ ...editFormData, jersey_number: e.target.value })}
                  placeholder="e.g. 10"
                  data-testid="edit-player-jersey-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-player-status">Status</Label>
                <Select
                  value={editFormData.status}
                  onValueChange={(value) => setEditFormData({ ...editFormData, status: value })}
                >
                  <SelectTrigger id="edit-player-status" data-testid="edit-player-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLAYER_STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)} data-testid="edit-player-cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  disabled={!editFormData.name.trim()}
                  className="btn-primary"
                  data-testid="edit-player-save-btn"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
